import {
	getMipData,
	isAnyInvitedInvestorInMIP,
	updateMipIsLocked,
	updateMipKycRequired,
} from '@dealstack/frontend/shared/dataExchange/services/god.service';
import { logger } from '@dealstack/frontend/shared/mechanism/logger/logger';
import { useDSToast } from '@dealstack/frontend/shared/mechanism/toast/useDSToast';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';

export const useManageMip = () => {
	const { mipId } = useParams();
	const toastAPI = useDSToast();

	const getMipDataRequest = useQuery({
		queryKey: [formatUnicorn(API_PATHS.GODMODE_MIP_MIPID_GET, { mipId: mipId || '' })],
		queryFn: ({ signal }) =>
			getMipData(
				formatUnicorn(API_PATHS.GODMODE_MIP_MIPID_GET, { mipId: mipId || '' }),
				signal
			),
	});

	const getIsAnyInvitedInvestorInMIPRequest = useQuery({
		queryKey: [
			formatUnicorn(API_PATHS.GODMODE_MIP_MIPID_IS_ANY_INVESTOR_INVITED_GET, {
				mipId: mipId || '',
			}),
		],

		queryFn: ({ signal }) =>
			isAnyInvitedInvestorInMIP(
				formatUnicorn(API_PATHS.GODMODE_MIP_MIPID_IS_ANY_INVESTOR_INVITED_GET, {
					mipId: mipId || '',
				}),
				signal
			),
		enabled: !!mipId,
	});

	const updateMipIsLockedRequest = useMutation({
		mutationFn: updateMipIsLocked,
	});
	const updateMipIsKycRequiredRequest = useMutation({
		mutationFn: updateMipKycRequired,
	});

	const refetchMipData = useCallback(() => {
		void getMipDataRequest.refetch();
	}, [getMipDataRequest?.data?.payload]);

	const onLockButtonSubmit = () => {
		if (!mipId) {
			return;
		}

		updateMipIsLockedRequest.mutate(
			{
				mipId,
				body: {
					isLocked: !getMipDataRequest?.data?.payload?.isLocked,
				},
			},
			{
				onError: (error) => {
					logger.error(error);
					toastAPI.error({
						message: "Data couldn't be updated!",
					});
				},
				onSuccess: () => {
					refetchMipData();
					toastAPI.success({
						message: 'Successfully updated!',
					});
				},
			}
		);
	};

	const onKycButtonSubmit = () => {
		if (!mipId) {
			return;
		}
		updateMipIsKycRequiredRequest.mutate(
			{
				mipId,
				body: {
					isKycRequired: !getMipDataRequest?.data?.payload?.isKycRequired,
				},
			},
			{
				onError: (error) => {
					logger.error(error);
					toastAPI.error({
						message: "Data couldn't be updated!",
					});
				},
				onSuccess: () => {
					refetchMipData();
					toastAPI.success({
						message: 'Successfully updated!',
					});
				},
			}
		);
	};

	useEffect(() => {
		if (!getMipDataRequest.isError) {
			return;
		}

		logger.error(getMipDataRequest.isError);
		toastAPI.error({
			message: 'Data cannot be shown!',
		});
	}, [getMipDataRequest.isError]);

	return {
		mipData: getMipDataRequest?.data?.payload,
		isAnyInvestorInvited: getIsAnyInvitedInvestorInMIPRequest?.data?.payload?.isInvited,
		isDataLoading: getMipDataRequest.isLoading,
		refetchMipData,
		onLockButtonSubmit,
		onKycButtonSubmit,
	};
};
